import { Koi } from '@ksyos/koi';
import { ConclusieDiagnoseOption } from '../../enums/conclusieDiagnoseOption';
import { TherapielandTreatmentCategory } from '../../enums/therapielandTreatmentCategory';
import { finalCallBackMoment, partialCallBackMomentAddEditObject } from '../callBackMoment';
import { careProviderContactDraft, careProviderContactFinal } from '../careProviderContact';
import { patientContactInfoDraft, patientContactInfoFinal } from '../patientContactInfo';
import { replaceKoiError } from '../replaceKoiError';
import { emailSchema } from '../schemas/emailSchema';

export const ggzcgV1DraftRequest = Koi.object().keys({
    conclusie_diagnose: Koi.koi().enum(ConclusieDiagnoseOption).allow(null),
    contactWithRequesterOptions: Koi.string().max(100000).allow(null),
    toelichting: Koi.string().max(10000).allow(null),
    vraag: Koi.string().max(10000).allow(null),
});

export const ggzcgV1FinalRequest = Koi.object().keys({
    conclusie_diagnose: Koi.koi().enum(ConclusieDiagnoseOption).allow(null),
    contactWithRequesterOptions: Koi.string().max(100000).allow(null),
    toelichting: Koi.string().max(10000).allow(null),
    vraag: Koi.string().max(10000),
});

export const ggzcmdoV1DraftRequest = ggzcgV1DraftRequest;
export const ggzcmdoV1FinalRequest = ggzcgV1FinalRequest;

export const ggzcf2fV1DraftRequest = ggzcgV1DraftRequest.concat(
    Koi.object().keys({
        contactgegevens_patient: patientContactInfoDraft,
    })
);

export const ggzcf2fV1FinalRequest = ggzcgV1FinalRequest.concat(
    Koi.object().keys({
        contactgegevens_patient: patientContactInfoFinal.concat(
            Koi.object().keys({ emailadres: emailSchema.allow(null) })
        ),
    })
);

export const ggzctV1DraftRequest = Koi.object().keys({
    beschikbaarheid: Koi.array().items(partialCallBackMomentAddEditObject).length(7),
    conclusie_diagnose: Koi.koi().enum(ConclusieDiagnoseOption).allow(null),
    contactgegevens_specialist: careProviderContactDraft,
    remarks: Koi.string().max(10000).allow(null),
    toelichting: Koi.string().max(10000).allow(null),
    vraag: Koi.string().max(10000).allow(null),
});

const ggzctFinalOnePeriodChecked = Koi.object().keys({
    dayOfWeek: Koi.any(),
    isChecked: Koi.valid(true),
    periodOne: Koi.any(),
    periodTwo: Koi.any(),
});

export const ggzctV1FinalRequest = Koi.object().keys({
    beschikbaarheid: Koi.array()
        .has(ggzctFinalOnePeriodChecked)
        .items(finalCallBackMoment)
        .length(7)
        .error((errors) => replaceKoiError(errors, 'array.hasUnknown', 'oneDayPeriodRequired')),
    conclusie_diagnose: Koi.koi().enum(ConclusieDiagnoseOption).allow(null),
    contactgegevens_specialist: careProviderContactFinal,
    remarks: Koi.string().max(10000).allow(null),
    toelichting: Koi.string().max(10000).allow(null),
    vraag: Koi.string().max(10000),
});

export const ggzctriageV1DraftRequest = ggzcgV1DraftRequest.concat(
    Koi.object().keys({
        contactgegevens_patient: patientContactInfoDraft,
    })
);

export const ggzctriageV1FinalRequest = ggzcgV1FinalRequest.concat(
    Koi.object().keys({
        contactgegevens_patient: patientContactInfoFinal,
    })
);

export const echelonOptions = ['Generalistische Basis GGZ', 'Specialistische GGZ'];

export const ggzvV1DraftRequest = Koi.object().keys({
    anamnese: Koi.string().max(10000).allow(null),
    besproken_met_patient: Koi.string().max(10000).allow(null),
    contactgegevens_patient: patientContactInfoDraft,
    context: Koi.string().max(10000).allow(null),
    echelon: Koi.string()
        .valid(...echelonOptions)
        .allow(null),
    huidig_beleid: Koi.string().max(10000).allow(null),
    klacht_of_hulpvraag: Koi.string().max(10000).allow(null),
    ook_bekend_bij: Koi.string().max(10000).allow(null),
    procedurevoorstel: Koi.string().max(10000).allow(null),
    relevante_comorbiditeit: Koi.string().max(10000).allow(null),
    risicovol_gedrag: Koi.string().max(10000).allow(null),
    suicidaliteit_en_psychose: Koi.string().max(10000).allow(null),
    toestemming: Koi.boolean(),
    vermoeden_van_dsm_stoornis: Koi.string().max(10000).allow(null),
});

export const ggzvV1FinalRequest = ggzvV1DraftRequest.concat(
    Koi.object().keys({
        contactgegevens_patient: patientContactInfoFinal,
        echelon: Koi.string()
            .valid(...echelonOptions)
            .disallow(null),
        toestemming: Koi.boolean().valid(true),
        vermoeden_van_dsm_stoornis: Koi.string().max(10000).disallow(null),
    })
);

export const ggzoIntV1DraftRequest = Koi.object().keys({
    emailadres: emailSchema.allow(null),
    heeftNameting: Koi.boolean(),
    nameting: Koi.number()
        .max(999)
        .min(1)
        .when('heeftNameting', { is: true, otherwise: Koi.allow(null), then: Koi.disallow(null) })
        .error((errors) => replaceKoiError(errors, 'number.base', 'weeks.required')),
    nametingOp: Koi.koi()
        .date()
        .when('heeftNameting', { is: true, otherwise: Koi.valid(null), then: Koi.disallow(null) }),
});

export const ggzoIntV1FinalRequest = ggzoIntV1DraftRequest.concat(
    Koi.object().keys({
        emailadres: Koi.disallow(null),
    })
);

export const ggzV1IntakeReport = Koi.object().keys({
    intake: Koi.string().max(100000).allow(null),
});

export const ggzV1DischargeReport = Koi.object().keys({
    dischargeLetter: Koi.string().max(100000).allow(null),
});

export const ggzoEmblV1DraftRequest = ggzoIntV1DraftRequest.concat(
    Koi.object({ vragenlijst: Koi.string().uuid(), vragenlijstLabel: Koi.string().max(255).allow(null) })
);
export const ggzoEmblV1FinalRequest = ggzoIntV1FinalRequest.concat(
    Koi.object({ vragenlijst: Koi.string().uuid(), vragenlijstLabel: Koi.string().max(255).allow(null) })
);

export const ggzbTherCheckedTreatments = Koi.array().items(
    Koi.object().keys({
        category: Koi.koi().enum(TherapielandTreatmentCategory),
        id: Koi.number(),
        treatmentName: Koi.string(),
    })
);

export const ggzbTherV1DraftRequest = Koi.object().keys({
    checkedTreatments: ggzbTherCheckedTreatments,
    emailadres: emailSchema.allow(null),
    identifierSocialSecurity: Koi.string().elfproef().allow(null),
    nameGiven: Koi.string().max(255).allow(null),
});

export const ggzbTherV1FinalRequest = ggzbTherV1DraftRequest.concat(
    Koi.object().keys({
        checkedTreatments: ggzbTherCheckedTreatments.min(1),
        emailadres: Koi.disallow(null),
        identifierSocialSecurity: Koi.string().elfproef().disallow(null),
        nameGiven: Koi.string().max(255).disallow(null),
    })
);

export const ggzcgV1FinalDiagnosticReport = Koi.object().keys({
    beleid: Koi.string().max(10000),
    bevindingen: Koi.string().max(10000),
    bezoek_aan_specialist_nodig: Koi.string().valid('Ja', 'Nee', 'Zie beleid'),
    conclusie_diagnose: Koi.koi().enum(ConclusieDiagnoseOption),
    toelichting: Koi.string().max(10000).allow(null),
});

export const ggzcgV1DraftDiagnosticReport = ggzcgV1FinalDiagnosticReport.concat(
    Koi.object().keys({
        beleid: Koi.any().allow(null),
        bevindingen: Koi.any().allow(null),
        bezoek_aan_specialist_nodig: Koi.any().allow(null),
        conclusie_diagnose: Koi.koi().enum(ConclusieDiagnoseOption).allow(null),
        toelichting: Koi.any().allow(null),
    })
);

export const ggzctriageV1FinalDiagnosticReport = Koi.object().keys({
    toelichting: Koi.string().max(10000).allow(null),
});

export const ggzctriageV1DraftDiagnosticReport = ggzctriageV1FinalDiagnosticReport;

export const ggzbTherV1NewEmailAdres = Koi.object().keys({
    newEmailadres: emailSchema.disallow(null),
});
